/**
 * This store provides the current state of the software.
 *
 */

import produce, { Draft } from "immer";
import { create } from "zustand";
import { saveMainStore } from "../utility/cache/manageMainStore";
import { CheckChangesMade } from "../utility/objectChangesMade";
import useAccountManagementStore from "./accountManagementStore";

export type SortNotesBy = "Date Created" | "Date Updated" | "Title";

export const defaultData: MainStoreType["data"] = {
    userID: "default",
    loaded: false,
    leftSidebarCollapsed: false,
    openedNoteIDs: [],
    selectedNoteID: "",
    leftSidebarWidth: 300,
    sortNotesBy: "Date Updated",
    sortNotesNewToOld: false,
    sortFoldersBy: "Date Updated",
    sortFoldersNewToOld: false,
    filterString: "", //* note stored in database
    showTableOfContents: false,
    selectedFolderID: undefined,
    syncState: {
        changesMade: [],
        isUploading: [],
    },
};

export interface MainStoreType {
    data: {
        userID: string;
        loaded?: boolean;
        leftSidebarWidth: number;
        leftSidebarCollapsed: boolean;
        openedNoteIDs: string[]; // Current notes opened as tabs
        selectedNoteID: string;
        sortNotesBy: SortNotesBy;
        sortNotesNewToOld: boolean;
        sortFoldersBy: SortNotesBy;
        sortFoldersNewToOld: boolean;
        filterString: string;
        showTableOfContents: boolean;
        selectedFolderID?: string | undefined;
        syncState?: {
            changesMade?: (
                | "tableChanged" // Table changed means dataStore changed, not the note contents
                | "tableRemoved"
                | "noteChanged" // noteChanged means the note contents changed
                | "noteRemoved"
            )[];
            isUploading?: (
                | "tableUpload"
                | "tableRemove"
                | "noteUpload"
                | "noteRemove"
            )[];
        };
    };
    temp?: {
        copyHTML: string | undefined;
    };

    open: {
        openNote: (noteID: string) => void;
        replaceSelectedNote: (noteID: string) => void;
    };

    set: {
        data: (
            fn: (data: MainStoreType["data"]) => MainStoreType["data"]
        ) => void;
        leftSidebarCollapsed: (fn: (data: boolean) => boolean) => void;
        openedNoteIDs: (fn: (openedNoteIDs: string[]) => string[]) => void;
        selectedNoteID: (fn: (data: string) => string) => void;
        setLeftSidebarWidth: (fn: (data: number) => number) => void;
        sortNotesBy: (fn: (data: SortNotesBy) => SortNotesBy) => void;
        sortNotesNewToOld: (fn: (data: boolean) => boolean) => void;
        sortFoldersBy: (fn: (data: SortNotesBy) => SortNotesBy) => void;
        sortFoldersNewToOld: (fn: (data: boolean) => boolean) => void;
        filterString: (fn: (data: string) => string) => void;
        showTableOfContents: (fn: (data: boolean) => boolean) => void;
        selectedFolderID: (
            fn: (data: string | undefined) => string | undefined
        ) => void;
        setTempHTML: (
            fn: (data: string | undefined) => string | undefined
        ) => void;
        syncState: (
            fn: (
                data: MainStoreType["data"]["syncState"]
            ) => MainStoreType["data"]["syncState"]
        ) => void;
    };
    actions: {
        unload: () => void;
    };
    syncFunctions: {
        addChangesMade: (
            change: MainStoreType["data"]["syncState"]["changesMade"][number]
        ) => void;
        removeChangesMade: (
            change: MainStoreType["data"]["syncState"]["changesMade"][number]
        ) => void;
        addIsUploading: (
            change: MainStoreType["data"]["syncState"]["isUploading"][number]
        ) => void;
        removeIsUploading: (
            change: MainStoreType["data"]["syncState"]["isUploading"][number]
        ) => void;
    };

    remove: {
        closeNote: (tabID: string) => void;
    };

    initialize: () => void;
}

const useMainStore = create<MainStoreType>((set, get) => ({
    data: defaultData,
    open: {
        openNote: (noteID) => {
            if (get().data.openedNoteIDs.includes(noteID)) {
                // If note is already open, select it
                // set(
                //     produce((draft: Draft<MainStoreType>) => {
                //         // draft.data.selectedNoteID = noteID;
                //     })
                // );
                get().set.selectedNoteID(() => noteID);

                return;
            }

            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.openedNoteIDs = [
                        ...draft.data.openedNoteIDs,
                        noteID,
                    ];
                    // draft.data.selectedNoteID = noteID;
                })
            );
            get().set.selectedNoteID(() => noteID);
        },
        replaceSelectedNote: (noteID) => {
            const selectedNote = get().data.selectedNoteID;

            // If noteID is already selected, do nothing
            if (noteID === selectedNote) return;
            // If noteID is already open, select it
            else if (get().data.openedNoteIDs.includes(noteID)) {
                get().set.selectedNoteID(() => noteID);
                return;
            }

            set(
                produce((draft: Draft<MainStoreType>) => {
                    // Else, select note and replace current opened note
                    // Replace current Note
                    let indexOfSelectedNote =
                        draft.data.openedNoteIDs.indexOf(selectedNote);

                    if (indexOfSelectedNote < 0) {
                        indexOfSelectedNote = 0;
                    }
                    draft.data.openedNoteIDs[indexOfSelectedNote] = noteID;

                    // Select note
                    // draft.data.selectedNoteID = noteID;
                })
            );
            get().set.selectedNoteID(() => noteID);
        },
    },

    set: {
        data: (fn) => {
            const newData = fn(get().data);
            let changesMade = CheckChangesMade(newData, get().data);
            const dataLoaded = get().data.loaded;

            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data = newData;
                })
            );

            if (changesMade && dataLoaded) {
                saveMainStore_();
            }
        },
        setLeftSidebarWidth: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.leftSidebarWidth = fn(
                        draft.data.leftSidebarWidth
                    );
                })
            );

            // Saved on mouse up to avoid repeated saves
        },
        leftSidebarCollapsed: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.leftSidebarCollapsed = fn(
                        draft.data.leftSidebarCollapsed
                    );
                })
            );

            saveMainStore_();
        },
        openedNoteIDs: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.openedNoteIDs = fn(draft.data.openedNoteIDs);
                })
            );
            saveMainStore_();
        },
        selectedNoteID: (fn) => {
            const selectedNoteID = fn(get().data.selectedNoteID);
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.selectedNoteID = selectedNoteID;
                    // console.log(selectedNoteID);
                })
            );
            saveMainStore_();
        },
        sortNotesBy: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.sortNotesBy = fn(draft.data.sortNotesBy);
                })
            );
            saveMainStore_();
        },
        sortNotesNewToOld: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.sortNotesNewToOld = fn(
                        draft.data.sortNotesNewToOld
                    );
                })
            );
            saveMainStore_();
        },
        filterString: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.filterString = fn(draft.data.filterString);
                })
            );
            saveMainStore_();
        },
        showTableOfContents: (fn) => {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.showTableOfContents = fn(
                        draft.data.showTableOfContents
                    );
                })
            );
            saveMainStore_();
        },
        selectedFolderID(fn) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.selectedFolderID = fn(
                        draft.data.selectedFolderID
                    );
                })
            );
            saveMainStore_();
        },
        sortFoldersBy(fn) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.sortFoldersBy = fn(draft.data.sortFoldersBy);
                })
            );
            saveMainStore_();
        },
        sortFoldersNewToOld(fn) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.sortFoldersNewToOld = fn(
                        draft.data.sortFoldersNewToOld
                    );
                })
            );
            saveMainStore_();
        },
        setTempHTML(fn) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.temp = draft.temp || { copyHTML: "" };
                    draft.temp.copyHTML = fn(draft.temp.copyHTML);
                })
            );
        },
        syncState(fn) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.syncState = fn(draft.data.syncState);
                })
            );
            saveMainStore_();
        },
    },
    syncFunctions: {
        addChangesMade(change) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    const currentChanges =
                        draft.data.syncState?.changesMade || [];
                    if (!currentChanges.includes(change as any)) {
                        draft.data.syncState = {
                            ...draft.data.syncState,
                            changesMade: [...currentChanges, change as any],
                        };
                    }
                })
            );
            saveMainStore_();
        },
        removeChangesMade(change) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    const currentChanges =
                        draft.data.syncState?.changesMade || [];
                    if (currentChanges.includes(change as any)) {
                        draft.data.syncState = {
                            ...draft.data.syncState,
                            changesMade: currentChanges.filter(
                                (c) => c !== change
                            ) as any,
                        };
                    }
                })
            );
            saveMainStore_();
        },
        addIsUploading(upload) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    const currentUploads =
                        draft.data.syncState?.isUploading || [];
                    if (!currentUploads.includes(upload as any)) {
                        draft.data.syncState = {
                            ...draft.data.syncState,
                            isUploading: [...currentUploads, upload as any],
                        };
                    }
                })
            );
            saveMainStore_();
        },
        removeIsUploading(upload) {
            set(
                produce((draft: Draft<MainStoreType>) => {
                    const currentUploads =
                        draft.data.syncState?.isUploading || [];
                    if (currentUploads.includes(upload as any)) {
                        draft.data.syncState = {
                            ...draft.data.syncState,
                            isUploading: currentUploads.filter(
                                (u) => u !== upload
                            ) as any,
                        };
                    }
                })
            );
            saveMainStore_();
        },
    },
    actions: {
        unload: () =>
            set((state) => ({
                data: { ...state.data, loaded: false },
            })),
    },
    remove: {
        closeNote: (tabID) => {
            if (!get().data.openedNoteIDs.includes(tabID)) return;

            set(
                produce((draft: Draft<MainStoreType>) => {
                    draft.data.openedNoteIDs = draft.data.openedNoteIDs.filter(
                        (id) => id !== tabID
                    );

                    if (draft.data.selectedNoteID === tabID) {
                        draft.data.selectedNoteID = draft.data.openedNoteIDs[0];
                    }
                })
            );
            saveMainStore_();
        },
    },
    initialize: () => {
        const userID = useAccountManagementStore.getState().userData?.id;
        set({ data: { ...defaultData, userID: userID, loaded: true } });
        saveMainStore(userID);
    },
}));

// New function to pass userID easier
function saveMainStore_() {
    const userID = useAccountManagementStore.getState().userData?.id;
    saveMainStore(userID);
}

export default useMainStore;

// useMainStore.subscribe((state) => {
//     // debouncedCallback(); // actually call the debounced function
// });

// const debouncedCallback = debounce(() => {
//     console.log("Saved Main Store");
//     saveMainStore();
// }, 100);
